import { useState, useEffect, useRef } from "react";
import Api from '../api/Api';

const COLORS = {
  primary: "#FF6B00",
  primaryDark: "#E55A00",
  bg: "#F4F6FA",
  dark: "#0F0C29",
  card: "#FFFFFF",
  text: "#111827",
  muted: "#6B7280",
  border: "#E5E7EB",
  success: "#00C853",
  danger: "#EF4444",
  dangerBg: "rgba(239,68,68,0.08)",
};

function formatPrice(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return "0";
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

// ─── Standalone field components (outside modal to prevent focus loss) ─────────

function Field({ label, required, error, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{
        display: "block", fontSize: 11, fontWeight: 800,
        color: COLORS.text, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5,
      }}>
        {label}{required && <span style={{ color: COLORS.danger }}> *</span>}
      </label>
      {children}
      {error && (
        <div style={{ fontSize: 11, color: COLORS.danger, marginTop: 4, fontWeight: 700 }}>⚠ {error}</div>
      )}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = "text", error }) {
  return (
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        width: "100%", padding: "11px 14px", borderRadius: 10,
        border: `2px solid ${error ? COLORS.danger : COLORS.border}`,
        outline: "none", fontFamily: "'Nunito', sans-serif",
        fontSize: 13, color: COLORS.text, boxSizing: "border-box",
        transition: "border-color 0.2s", background: "#fff",
      }}
      onFocus={e => e.target.style.borderColor = COLORS.primary}
      onBlur={e => e.target.style.borderColor = error ? COLORS.danger : COLORS.border}
    />
  );
}

function TextArea({ value, onChange, placeholder, error }) {
  return (
    <textarea
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      rows={3}
      style={{
        width: "100%", padding: "11px 14px", borderRadius: 10,
        border: `2px solid ${error ? COLORS.danger : COLORS.border}`,
        outline: "none", fontFamily: "'Nunito', sans-serif",
        fontSize: 13, color: COLORS.text, boxSizing: "border-box",
        resize: "vertical", transition: "border-color 0.2s", background: "#fff",
      }}
      onFocus={e => e.target.style.borderColor = COLORS.primary}
      onBlur={e => e.target.style.borderColor = error ? COLORS.danger : COLORS.border}
    />
  );
}

// ─── Product Form Modal ────────────────────────────────────────────────────────

function ProductModal({ mode, product, onClose, onSuccess }) {
  const emptyForm = { nome: "", categoria: "", preco: "", anterior: "", descricao: "", badge: "", badgecolor: "#FF6B00", tag: "" };
  const [form, setForm] = useState(mode === "edit" && product ? {
    nome: product.nome || "",
    categoria: product.categoria || "",
    preco: product.preco || "",
    anterior: product.anterior || "",
    descricao: product.descricao || "",
    badge: product.badge || "",
    badgecolor: product.badgecolor || "#FF6B00",
    tag: product.tag || "",
  } : emptyForm);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(mode === "edit" ? product?.img : null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const fileRef = useRef();

  const set = (field) => (value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setErrors(prev => ({ ...prev, file: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.nome.trim()) e.nome = "Nome obrigatório";
    if (!form.preco) e.preco = "Preço obrigatório";
    if (!form.anterior) e.anterior = "Preço anterior obrigatório";
    if (mode === "add" && !file) e.file = "Imagem obrigatória";
    return e;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    setServerError("");
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      fd.append("nome", form.nome);
      fd.append("categoria", form.categoria);
      fd.append("preco", form.preco);
      fd.append("anterior", form.anterior);
      fd.append("descricao", form.descricao);
      fd.append("badge", form.badge);
      fd.append("badgecolor", form.badgecolor);
      fd.append("tag", form.tag);
      if (mode === "edit") fd.append("id", product.id);

      if (mode === "add") {
        await Api.AddProduto(fd);
      } else {
        await Api.AlterProduto(fd);
      }
      onSuccess();
      onClose();
    } catch (err) {
      setServerError("Erro ao salvar produto. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(6px)",
        zIndex: 10000,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 24,
          width: "100%", maxWidth: 540,
          maxHeight: "92vh", overflowY: "auto",
          boxShadow: "0 32px 80px rgba(0,0,0,0.3)",
          animation: "slideUpModal 0.3s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${COLORS.primary}, #FF3D00)`,
          padding: "22px 24px 20px",
          borderRadius: "24px 24px 0 0",
          position: "relative",
        }}>
          <button onClick={onClose} style={{
            position: "absolute", top: 14, right: 14,
            background: "rgba(255,255,255,0.2)", border: "none",
            color: "#fff", width: 30, height: 30, borderRadius: "50%",
            cursor: "pointer", fontSize: 14,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>✕</button>
          <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 10, fontWeight: 800, letterSpacing: 2, marginBottom: 4 }}>
            {mode === "add" ? "NOVO PRODUTO" : "EDITAR PRODUTO"}
          </div>
          <div style={{ color: "#fff", fontSize: 18, fontWeight: 900 }}>
            {mode === "add" ? "➕ Adicionar Produto" : `✏️ ${product?.nome}`}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "24px 24px 28px" }}>
          {serverError && (
            <div style={{
              background: COLORS.dangerBg, border: `1px solid rgba(239,68,68,0.3)`,
              borderRadius: 10, padding: "10px 14px", marginBottom: 18,
              fontSize: 13, color: COLORS.danger, fontWeight: 700,
            }}>⚠️ {serverError}</div>
          )}

          {/* Image upload */}
          <Field label="Imagem do Produto" required={mode === "add"} error={errors.file}>
            <div
              onClick={() => fileRef.current.click()}
              style={{
                border: `2px dashed ${errors.file ? COLORS.danger : preview ? COLORS.primary : COLORS.border}`,
                borderRadius: 12, padding: preview ? 0 : "24px 16px",
                textAlign: "center", cursor: "pointer",
                overflow: "hidden", transition: "border-color 0.2s",
                background: preview ? "#000" : "#FAFAFA",
                minHeight: preview ? 140 : "auto",
                display: "flex", alignItems: "center", justifyContent: "center",
                position: "relative",
              }}
            >
              {preview ? (
                <>
                  <img src={preview} alt="preview" style={{ width: "100%", maxHeight: 160, objectFit: "cover", display: "block", opacity: 0.85 }} />
                  <div style={{
                    position: "absolute", inset: 0, display: "flex",
                    alignItems: "center", justifyContent: "center",
                    background: "rgba(0,0,0,0.4)",
                    opacity: 0, transition: "opacity 0.2s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.opacity = 1}
                    onMouseLeave={e => e.currentTarget.style.opacity = 0}
                  >
                    <span style={{ color: "#fff", fontWeight: 800, fontSize: 13 }}>🔄 Trocar imagem</span>
                  </div>
                </>
              ) : (
                <div>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🖼️</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.muted }}>Clique para selecionar imagem</div>
                  <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>JPG, PNG até 5MB</div>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display: "none" }} />
          </Field>

          {/* Two columns for mobile-friendly layout */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Nome do Produto" required error={errors.nome}>
                <TextInput value={form.nome} onChange={set("nome")} placeholder="Ex: iPhone 17 Pro" error={errors.nome} />
              </Field>
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Categoria" error={errors.categoria}>
                <TextInput value={form.categoria} onChange={set("categoria")} placeholder="Ex: Telefones" error={errors.categoria} />
              </Field>
            </div>

            <Field label="Preço (Kz)" required error={errors.preco}>
              <TextInput value={form.preco} onChange={set("preco")} placeholder="5000" type="number" error={errors.preco} />
            </Field>

            <Field label="Preço Anterior (Kz)" required error={errors.anterior}>
              <TextInput value={form.anterior} onChange={set("anterior")} placeholder="10000" type="number" error={errors.anterior} />
            </Field>
          </div>

          <Field label="Descrição" error={errors.descricao}>
            <TextArea value={form.descricao} onChange={set("descricao")} placeholder="Descrição detalhada do produto..." error={errors.descricao} />
          </Field>

   

    

          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              width: "100%",
              background: loading ? "rgba(255,107,0,0.5)" : `linear-gradient(135deg, ${COLORS.primary}, #FF3D00)`,
              border: "none", color: "#fff",
              fontFamily: "'Nunito', sans-serif",
              fontSize: 15, fontWeight: 900,
              padding: "16px", borderRadius: 14,
              cursor: loading ? "not-allowed" : "pointer",
              boxShadow: loading ? "none" : "0 8px 24px rgba(255,107,0,0.4)",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            }}
          >
            {loading ? (
              <>
                <div style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                A guardar...
              </>
            ) : mode === "add" ? "➕ Adicionar Produto" : "💾 Guardar Alterações"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delete Confirmation Modal ────────────────────────────────────────────────

function DeleteModal({ product, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleDelete = async () => {
    setLoading(true);
    try {
      await Api.DeleteProduto({id:product.id});
      onSuccess();
      onClose();
    } catch {
      setError("Erro ao eliminar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)",
        zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 24, width: "100%", maxWidth: 380,
          padding: "32px 28px", textAlign: "center",
          boxShadow: "0 32px 80px rgba(0,0,0,0.3)",
          animation: "slideUpModal 0.3s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        <div style={{
          width: 68, height: 68, borderRadius: "50%",
          background: "rgba(239,68,68,0.1)", border: "2px solid rgba(239,68,68,0.2)",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 20px", fontSize: 28,
        }}>🗑️</div>
        <h3 style={{ fontSize: 18, fontWeight: 900, color: COLORS.text, marginBottom: 10 }}>Eliminar Produto?</h3>
        <p style={{ fontSize: 13, color: COLORS.muted, lineHeight: 1.7, marginBottom: 8 }}>
          Tens a certeza que queres eliminar <strong>"{product?.nome}"</strong>? Esta acção não pode ser revertida.
        </p>
        {error && <div style={{ fontSize: 12, color: COLORS.danger, fontWeight: 700, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: "13px", borderRadius: 12,
            border: `2px solid ${COLORS.border}`, background: "#fff",
            fontFamily: "'Nunito', sans-serif", fontSize: 13, fontWeight: 800,
            cursor: "pointer", color: COLORS.muted,
          }}>Cancelar</button>
          <button onClick={handleDelete} disabled={loading} style={{
            flex: 1, padding: "13px", borderRadius: 12, border: "none",
            background: loading ? "rgba(239,68,68,0.5)" : "linear-gradient(135deg, #EF4444, #DC2626)",
            fontFamily: "'Nunito', sans-serif", fontSize: 13, fontWeight: 900,
            cursor: loading ? "not-allowed" : "pointer", color: "#fff",
            boxShadow: "0 4px 14px rgba(239,68,68,0.35)",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            {loading ? (
              <div style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
            ) : "🗑️ Eliminar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{
      position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
      zIndex: 99999,
      background: type === "success"
        ? "linear-gradient(135deg, #00C853, #00A846)"
        : "linear-gradient(135deg, #EF4444, #DC2626)",
      color: "#fff", borderRadius: 50,
      padding: "14px 24px",
      fontFamily: "'Nunito', sans-serif", fontSize: 14, fontWeight: 800,
      boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
      animation: "fadeSlideUp 0.3s ease",
      whiteSpace: "nowrap",
      display: "flex", alignItems: "center", gap: 8,
    }}>
      {type === "success" ? "✅" : "❌"} {message}
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function TableSkeleton() {
  return (
    <>
      {[1, 2, 3, 4].map(i => (
        <tr key={i}>
          <td style={{ padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: 10, background: "#f0f0f0", flexShrink: 0, animation: "shimmer 1.4s infinite" }} />
              <div>
                <div style={{ width: 120, height: 13, background: "#f0f0f0", borderRadius: 6, marginBottom: 6, animation: "shimmer 1.4s infinite" }} />
                <div style={{ width: 80, height: 11, background: "#f0f0f0", borderRadius: 6, animation: "shimmer 1.4s infinite" }} />
              </div>
            </div>
          </td>
          <td style={{ padding: "14px 16px" }}><div style={{ width: 60, height: 13, background: "#f0f0f0", borderRadius: 6, animation: "shimmer 1.4s infinite" }} /></td>
          <td style={{ padding: "14px 16px" }}><div style={{ width: 70, height: 13, background: "#f0f0f0", borderRadius: 6, animation: "shimmer 1.4s infinite" }} /></td>
          <td style={{ padding: "14px 16px" }}><div style={{ width: 80, height: 24, background: "#f0f0f0", borderRadius: 20, animation: "shimmer 1.4s infinite" }} /></td>
          <td style={{ padding: "14px 16px" }}><div style={{ width: 80, height: 32, background: "#f0f0f0", borderRadius: 8, animation: "shimmer 1.4s infinite" }} /></td>
        </tr>
      ))}
    </>
  );
}

// ─── Mobile Product Card ──────────────────────────────────────────────────────

function MobileCard({ product, onEdit, onDelete }) {
  const discount = Math.round((1 - parseFloat(product.preco) / parseFloat(product.anterior)) * 100);
  return (
    <div style={{
      background: "#fff", borderRadius: 16,
      boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
      overflow: "hidden", marginBottom: 12,
      border: `1px solid ${COLORS.border}`,
    }}>
      <div style={{ display: "flex", gap: 12, padding: "14px 14px 0" }}>
        <img
          src={product.img} alt={product.nome}
          style={{ width: 64, height: 64, borderRadius: 10, objectFit: "cover", flexShrink: 0, background: "#F4F6FA" }}
          onError={e => { e.target.src = "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=200&q=60"; }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
            <h3 style={{ fontSize: 14, fontWeight: 900, color: COLORS.text, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {product.nome}
            </h3>
            {product.badge && (
              <span style={{
                background: product.badgecolor || COLORS.primary,
                color: "#fff", fontSize: 9, fontWeight: 800,
                padding: "3px 8px", borderRadius: 20, flexShrink: 0,
              }}>{product.badge}</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: COLORS.muted, fontWeight: 700, marginBottom: 4 }}>{product.categoria}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 15, fontWeight: 900, color: COLORS.primary }}>Kz {formatPrice(product.preco)}</span>
            <span style={{ fontSize: 10, color: COLORS.muted, textDecoration: "line-through" }}>Kz {formatPrice(product.anterior)}</span>
            <span style={{
              fontSize: 9, fontWeight: 800, color: "#fff",
              background: "linear-gradient(135deg,#FF6B00,#FF3D00)",
              padding: "2px 7px", borderRadius: 20,
            }}>-{discount}%</span>
          </div>
        </div>
      </div>
      <div style={{
        display: "flex", gap: 8, padding: "12px 14px",
        borderTop: `1px solid ${COLORS.border}`, marginTop: 12,
      }}>
        <button onClick={() => onEdit(product)} style={{
          flex: 1, padding: "10px", borderRadius: 10, border: "none",
          background: "rgba(255,107,0,0.08)", color: COLORS.primary,
          fontFamily: "'Nunito', sans-serif", fontSize: 12, fontWeight: 800,
          cursor: "pointer",
        }}>✏️ Editar</button>
        <button onClick={() => onDelete(product)} style={{
          flex: 1, padding: "10px", borderRadius: 10, border: "none",
          background: COLORS.dangerBg, color: COLORS.danger,
          fontFamily: "'Nunito', sans-serif", fontSize: 12, fontWeight: 800,
          cursor: "pointer",
        }}>🗑️ Eliminar</button>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────

export default function Produtos() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null); // { type: "add"|"edit"|"delete", product? }
  const [toast, setToast] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    const style = document.createElement("style");
    style.textContent = `
      @keyframes slideUpModal { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
      @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { background: #F4F6FA; }
      ::-webkit-scrollbar { width: 4px; height: 4px; }
      ::-webkit-scrollbar-thumb { background: #FF6B00; border-radius: 10px; }
    `;
    document.head.appendChild(style);

    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handleResize);

    loadProducts();
    setTimeout(() => setMounted(true), 80);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await Api.ListProduto();
      setProducts(data);
    } catch {
      showToast("Erro ao carregar produtos", "error");
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  const filtered = products.filter(p => {
    const q = search.toLowerCase();
    return p.nome.toLowerCase().includes(q) || p.categoria?.toLowerCase().includes(q);
  });

  const handleSuccess = (msg) => {
    showToast(msg || "Operação realizada com sucesso!");
    loadProducts();
  };

  return (
    <div style={{
      minHeight: "100vh", background: COLORS.bg,
      fontFamily: "'Nunito', sans-serif",
      opacity: mounted ? 1 : 0, transition: "opacity 0.4s ease",
    }}>

      {/* ── NAVBAR ── */}
      <nav style={{
        background: "#fff",
        boxShadow: "0 2px 20px rgba(0,0,0,0.08)",
        padding: "0 16px",
        display: "flex", alignItems: "center", gap: 12,
        position: "sticky", top: 0, zIndex: 200, height: 64,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <div style={{
            width: 38, height: 38,
            background: "linear-gradient(135deg, #FF6B00, #FF3D00)",
            borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, fontWeight: 900, color: "#fff",
            boxShadow: "0 4px 12px rgba(255,107,0,0.4)",
          }}>S</div>
          <div>
            <div style={{ fontWeight: 900, fontSize: 16, color: COLORS.text, letterSpacing: -0.5, lineHeight: 1.1 }}>SATEC</div>
            <div style={{ fontSize: 8, color: COLORS.primary, fontWeight: 700, letterSpacing: 2 }}>IMPORTS</div>
          </div>
        </div>

        <div style={{ flex: 1, fontWeight: 900, fontSize: 15, color: COLORS.text }}>
          Gestão de Produtos
        </div>

        <button
          onClick={() => setModal({ type: "add" })}
          style={{
            background: "linear-gradient(135deg, #FF6B00, #FF3D00)",
            border: "none", color: "#fff",
            fontFamily: "'Nunito', sans-serif",
            fontSize: 12, fontWeight: 800,
            padding: "10px 16px", borderRadius: 50,
            cursor: "pointer",
            boxShadow: "0 4px 14px rgba(255,107,0,0.4)",
            display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
          }}
        >
          ➕ {!isMobile && "Novo Produto"}
        </button>
      </nav>

      {/* ── MAIN ── */}
      <main style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 16px 60px" }}>

        {/* Stats bar */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
          gap: 12, marginBottom: 24,
        }}>
          {[
            { label: "Total Produtos", value: products.length, icon: "📦", color: COLORS.primary },
            { label: "Categorias", value: new Set(products.map(p => p.categoria)).size, icon: "🏷️", color: "#0066FF" },
            { label: "Mais barato", value: products.length ? `Kz ${formatPrice(Math.min(...products.map(p => parseFloat(p.preco))))}` : "—", icon: "💰", color: COLORS.success },
            { label: "Mais caro", value: products.length ? `Kz ${formatPrice(Math.max(...products.map(p => parseFloat(p.preco))))}` : "—", icon: "💎", color: "#7C3AED" },
          ].map(s => (
            <div key={s.label} style={{
              background: "#fff", borderRadius: 16, padding: "16px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
              animation: "fadeSlideUp 0.4s ease",
            }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: s.color, marginBottom: 2 }}>{s.value}</div>
              <div style={{ fontSize: 11, color: COLORS.muted, fontWeight: 700 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Search + header */}
        <div style={{
          background: "#fff", borderRadius: 16,
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          overflow: "hidden",
        }}>
          <div style={{
            padding: "16px 20px",
            borderBottom: `1px solid ${COLORS.border}`,
            display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
          }}>
            <div style={{
              flex: 1, minWidth: 180,
              display: "flex", alignItems: "center",
              background: COLORS.bg, borderRadius: 50,
              border: `2px solid ${COLORS.border}`,
              padding: "0 14px", gap: 8,
            }}>
              <span style={{ fontSize: 14 }}>🔍</span>
              <input
                type="text"
                placeholder="Pesquisar produtos..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  flex: 1, border: "none", background: "transparent",
                  padding: "10px 0", fontSize: 13, outline: "none",
                  fontFamily: "'Nunito', sans-serif", color: COLORS.text,
                }}
              />
              {search && (
                <button onClick={() => setSearch("")} style={{ background: "none", border: "none", color: COLORS.muted, cursor: "pointer", fontSize: 16, lineHeight: 1 }}>✕</button>
              )}
            </div>
            <div style={{ fontSize: 12, color: COLORS.muted, fontWeight: 700, flexShrink: 0 }}>
              {loading ? "A carregar..." : `${filtered.length} produto${filtered.length !== 1 ? "s" : ""}`}
            </div>
          </div>

          {/* ── MOBILE: Cards ── */}
          {isMobile ? (
            <div style={{ padding: "16px" }}>
              {loading ? (
                [1, 2, 3].map(i => (
                  <div key={i} style={{ background: "#fff", borderRadius: 16, height: 120, marginBottom: 12, background: "#f0f0f0", animation: "shimmer 1.4s infinite" }} />
                ))
              ) : filtered.length === 0 ? (
                <div style={{ textAlign: "center", padding: "48px 20px" }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
                  <p style={{ fontSize: 14, fontWeight: 700, color: COLORS.muted }}>Nenhum produto encontrado</p>
                </div>
              ) : (
                filtered.map(p => (
                  <MobileCard
                    key={p.id}
                    product={p}
                    onEdit={prod => setModal({ type: "edit", product: prod })}
                    onDelete={prod => setModal({ type: "delete", product: prod })}
                  />
                ))
              )}
            </div>
          ) : (
            /* ── DESKTOP: Table ── */
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#FAFAFA", borderBottom: `2px solid ${COLORS.border}` }}>
                    {["Produto", "Categoria", "Preço", "Badge", "Ações"].map(h => (
                      <th key={h} style={{
                        padding: "12px 16px", textAlign: "left",
                        fontSize: 11, fontWeight: 800, color: COLORS.muted,
                        textTransform: "uppercase", letterSpacing: 0.8,
                        whiteSpace: "nowrap",
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <TableSkeleton />
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: "center", padding: "56px 20px" }}>
                        <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
                        <p style={{ fontSize: 14, fontWeight: 700, color: COLORS.muted }}>Nenhum produto encontrado</p>
                      </td>
                    </tr>
                  ) : (
                    filtered.map((p, idx) => {
                      const discount = Math.round((1 - parseFloat(p.preco) / parseFloat(p.anterior)) * 100);
                      return (
                        <tr key={p.id} style={{
                          borderBottom: `1px solid ${COLORS.border}`,
                          background: idx % 2 === 0 ? "#fff" : "#FDFCFC",
                          transition: "background 0.15s",
                        }}
                          onMouseEnter={e => e.currentTarget.style.background = "#FFF7ED"}
                          onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#FDFCFC"}
                        >
                          {/* Produto */}
                          <td style={{ padding: "12px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                              <img
                                src={p.img} alt={p.nome}
                                style={{ width: 52, height: 52, borderRadius: 10, objectFit: "cover", flexShrink: 0, background: "#F4F6FA" }}
                                onError={e => { e.target.src = "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=200&q=60"; }}
                              />
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 800, color: COLORS.text, marginBottom: 2 }}>{p.nome}</div>
                                {p.tag && <div style={{ fontSize: 11, color: COLORS.muted, fontWeight: 600 }}>{p.tag}</div>}
                              </div>
                            </div>
                          </td>

                          {/* Categoria */}
                          <td style={{ padding: "12px 16px" }}>
                            <span style={{
                              background: "#EEF2FF", color: "#4338CA",
                              fontSize: 11, fontWeight: 800,
                              padding: "4px 10px", borderRadius: 20,
                            }}>{p.categoria || "—"}</span>
                          </td>

                          {/* Preço */}
                          <td style={{ padding: "12px 16px" }}>
                            <div style={{ fontSize: 14, fontWeight: 900, color: COLORS.primary }}>Kz {formatPrice(p.preco)}</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                              <span style={{ fontSize: 11, color: COLORS.muted, textDecoration: "line-through" }}>Kz {formatPrice(p.anterior)}</span>
                              <span style={{
                                fontSize: 9, fontWeight: 900, color: "#fff",
                                background: "linear-gradient(135deg,#FF6B00,#FF3D00)",
                                padding: "1px 6px", borderRadius: 20,
                              }}>-{discount}%</span>
                            </div>
                          </td>

                          {/* Badge */}
                          <td style={{ padding: "12px 16px" }}>
                            {p.badge ? (
                              <span style={{
                                background: p.badgecolor || COLORS.primary,
                                color: "#fff", fontSize: 10, fontWeight: 800,
                                padding: "4px 12px", borderRadius: 20,
                              }}>{p.badge}</span>
                            ) : <span style={{ color: COLORS.muted, fontSize: 12 }}>—</span>}
                          </td>

                          {/* Ações */}
                          <td style={{ padding: "12px 16px" }}>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button
                                onClick={() => setModal({ type: "edit", product: p })}
                                style={{
                                  padding: "8px 14px", borderRadius: 8, border: "none",
                                  background: "rgba(255,107,0,0.08)", color: COLORS.primary,
                                  fontFamily: "'Nunito', sans-serif", fontSize: 12, fontWeight: 800,
                                  cursor: "pointer", transition: "all 0.15s",
                                }}
                                onMouseEnter={e => { e.target.background = "rgba(255,107,0,0.15)"; }}
                              >✏️ Editar</button>
                              <button
                                onClick={() => setModal({ type: "delete", product: p })}
                                style={{
                                  padding: "8px 14px", borderRadius: 8, border: "none",
                                  background: COLORS.dangerBg, color: COLORS.danger,
                                  fontFamily: "'Nunito', sans-serif", fontSize: 12, fontWeight: 800,
                                  cursor: "pointer",
                                }}
                              >🗑️</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* ── Modals ── */}
      {modal?.type === "add" && (
        <ProductModal
          mode="add"
          onClose={() => setModal(null)}
          onSuccess={() => handleSuccess("Produto adicionado com sucesso!")}
        />
      )}
      {modal?.type === "edit" && (
        <ProductModal
          mode="edit"
          product={modal.product}
          onClose={() => setModal(null)}
          onSuccess={() => handleSuccess("Produto actualizado com sucesso!")}
        />
      )}
      {modal?.type === "delete" && (
        <DeleteModal
          product={modal.product}
          onClose={() => setModal(null)}
          onSuccess={() => handleSuccess("Produto eliminado com sucesso!")}
        />
      )}

      {/* ── Toast ── */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}